import type { FormEvent } from 'react';
import React, { Suspense, useState } from 'react';
import { type MultiPageVideoModel, type VideoElement } from '@bynder-studio/render-core';
import { Divider, Form, List, Thumbnail } from '@bynder/design-system';
import { IconAdjust, IconArrowDown, IconArrowUp } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useAccessRights from 'packages/hooks/useAccessRights';
import useEditor from '~/hooks/useEditor';
import useEditorSelectedElement from '~/hooks/useEditorSelectedElement';
import useForceUpdate from '~/hooks/useForceUpdate';
import features from 'packages/configs/features';
import AuthorizationHelper from '~/helpers/AuthorizationHelper';
import { useEffectivelyLocked } from 'packages/hooks/useEffectivelyLocked';
import SettingsTabs from '../Tabs';
import ElementAlignment from '../ElementAlignment';
import Width from '../Size/Width';
import LockToggler from '../Size/LockToggle';
import Height from '../Size/Height';
import HorizontalPosition from '../Position/Horizontal';
import VerticalPosition from '../Position/Vertical';
import Rotation from '../Rotation';
import Opacity from '../Opacity';
import Duration from '../Duration';
import Masking from '../Masking';
import Shadow from '../Shadow';
import CropMode from '../CropMode';
import BrowseVideo from './BrowseVideo';
import { BlendMode } from '../BlendMode';
import AssetSource from '../AssetSource';
import Audio from './Audio';
import Animations from '../../Animations';
import PersonalUploads from '../PersonalUploads';
import AllowToggleVisibility from '../AllowToggleVisibility';
import ContentProperty from '../ContentProperty';
import { SizeFormRow } from '../index.styled';
import { FormSectionContent, InspectorContent, InspectorHeader, Section } from '../../index.styled';
import useEditorSidebarShortcuts from 'packages/hooks/shortcuts/useEditorSidebarShortcuts';

const Video = () => {
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { creativeModel, creativeType, manipulationRenderer, frameRate } = useEditor();
    const { selectedElement, selectedElements } = useEditorSelectedElement<VideoElement>();
    const isLockedByParent = useEffectivelyLocked();

    const tabs = [
        { value: 'video', label: translate('editor.sidebar.shots.video.tabs.text') },
        { value: 'video_animation', label: translate('editor.sidebar.shots.animation.tabs.text') },
    ];

    const [activeTab, setTab] = useState(tabs[0].value);
    const [videoSettingsExpanded, setVideoSettingsExpanded] = useState(false);

    const isInternalAssetsAvailable = AuthorizationHelper.isFeatureAvailable(features.INTERNAL_ASSET);

    const defaultProps = {
        creativeModel,
        selectedElement,
        disabled: isLockedByParent,
    };

    useEditorSidebarShortcuts({
        setAnimationsTab: () => {
            if (creativeType === 'VIDEO') {
                setTab(tabs[1].value);
            }
        },
    });

    return (
        <>
            {creativeType === 'VIDEO' && (
                <InspectorHeader aria-labelledby="shots-video-tabs-settings">
                    <SettingsTabs tabs={tabs} activeTab={activeTab} onChange={(value) => setTab(value)} />
                </InspectorHeader>
            )}
            <InspectorContent withTabs={creativeType === 'VIDEO'}>
                {activeTab === tabs[0].value && (
                    <>
                        <Section aria-labelledby="shots-alignment-settings">
                            <ElementAlignment creativeModel={creativeModel} selectedElements={selectedElements} />
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-size-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <SizeFormRow>
                                    <Width creativeType={creativeType} {...defaultProps} />
                                    <LockToggler selectedElement={selectedElement} forceUpdate={forceUpdate} />
                                    <Height creativeType={creativeType} {...defaultProps} />
                                </SizeFormRow>
                                <Form.Row>
                                    <HorizontalPosition {...defaultProps} />
                                    <VerticalPosition {...defaultProps} />
                                </Form.Row>
                                <Form.Row>
                                    <Rotation {...defaultProps} />
                                    <Opacity {...defaultProps} />
                                </Form.Row>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                            <>
                                <Section aria-labelledby="shots-text-content-property">
                                    <Suspense>
                                        <ContentProperty
                                            creativeModel={creativeModel}
                                            selectedElements={selectedElements}
                                            disabled={defaultProps.disabled}
                                        />
                                    </Suspense>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shots-video-asset-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <BrowseVideo
                                    creativeModel={creativeModel}
                                    selectedElement={selectedElement}
                                    frameRate={frameRate}
                                    disabled={isLockedByParent}
                                />
                                <List.Item
                                    thumbnail={<Thumbnail icon={<IconAdjust />} />}
                                    rightElements={videoSettingsExpanded ? <IconArrowUp /> : <IconArrowDown />}
                                    onClick={() => setVideoSettingsExpanded((state) => !state)}
                                >
                                    {translate('design.sidebar.scene.options.expand.label', {
                                        type: translate('editor.sidebar.shots.video.tabs.text'),
                                    })}
                                </List.Item>
                                <div style={videoSettingsExpanded ? undefined : { display: 'none' }}>
                                    <CropMode disabled={isLockedByParent} />
                                </div>
                                {videoSettingsExpanded && (
                                    <>
                                        <Divider />
                                        <Duration
                                            {...defaultProps}
                                            creativeModel={creativeModel as MultiPageVideoModel}
                                            frameRate={frameRate}
                                        />
                                        <Divider />
                                        <Audio {...defaultProps} />
                                    </>
                                )}
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-video-asset-source">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <AssetSource disabled={defaultProps.disabled} />
                                </Form.Group>
                                {isInternalAssetsAvailable && (
                                    <>
                                        <Divider />
                                        <Form.Group>
                                            <PersonalUploads
                                                selectedElements={[selectedElement]}
                                                disabled={defaultProps.disabled}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-video-visibility">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.visibility_control')}</Form.Label>
                                    <AllowToggleVisibility
                                        creativeModel={creativeModel}
                                        selectedElements={[selectedElement]}
                                        disabled={defaultProps.disabled}
                                    />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-video-masking-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.mask')}</Form.Label>
                                    <Masking
                                        {...defaultProps}
                                        creativeType={creativeType}
                                        manipulationRenderer={manipulationRenderer}
                                    />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-video-shadow">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.sidebar.shots.text.shadow.label')}</Form.Label>
                                    <Shadow {...defaultProps} />
                                </Form.Group>
                                <Divider />
                                <Form.Group>
                                    <BlendMode disabled={defaultProps.disabled} />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                    </>
                )}
                {activeTab === tabs[1].value && <Animations element={selectedElement} disabled={isLockedByParent} />}
            </InspectorContent>
        </>
    );
};

export default Video;

function preventSubmit(e: FormEvent) {
    e.preventDefault();
}
